import {
  ADMIN_LDAP_GROUP,
  AUDITOR_LDAP_GROUP,
  ENGINEER_LDAP_GROUP,
  GENERAL_USER_LDAP_GROUP,
  NAVIGATOR_LDAP_GROUPS,
  SIMULATE_LDAP_GROUPS_HEADER,
} from '../types/constants';
import { hasLength } from './dataTransformations';

export interface RoleFlags {
  isEngineer: boolean;
  isAdmin: boolean;
  isAuditor: boolean;
  hasPermission: boolean;
}

export const ldapToRoleFlags = (ldap: string[]): RoleFlags => {
  const isEngineer = ldap.includes(ENGINEER_LDAP_GROUP);
  const isAdmin = isEngineer || ldap.includes(ADMIN_LDAP_GROUP);
  // Auditing is a separate axis from the privilege levels, so it is not implied by admin.
  const isAuditor = ldap.includes(AUDITOR_LDAP_GROUP);

  return {
    isEngineer,
    isAdmin,
    isAuditor,
    // The auditor group grants app access on its own: an auditor need not also be a general user.
    hasPermission: isAdmin || isAuditor || ldap.includes(GENERAL_USER_LDAP_GROUP),
  };
};

const LDAP_GROUP_SEPARATOR = ',';

export const storedToLdapGroups = (stored: string | null): string[] =>
  Array.from(
    new Set(
      (stored ?? '')
        .split(LDAP_GROUP_SEPARATOR)
        .map((group) => group.trim())
        .filter((group) => group !== ''),
    ),
  );

export const ldapGroupsToStored = (groups: string[]): string => groups.join(LDAP_GROUP_SEPARATOR);

/** Engineers may simulate any Navigator group; admins may simulate their own groups or a regular user. */
export const realLdapToSimulatableLdap = (realLdap: string[]): string[] => {
  const { isEngineer, isAdmin } = ldapToRoleFlags(realLdap);

  if (isEngineer) return NAVIGATOR_LDAP_GROUPS;
  if (isAdmin) {
    return NAVIGATOR_LDAP_GROUPS.filter(
      (group) => group === GENERAL_USER_LDAP_GROUP || realLdap.includes(group),
    );
  }
  return [];
};

/** The requested groups when every one is allowed for the real user, otherwise no simulation. */
export const realLdapToSimulatedLdap =
  (realLdap: string[]) =>
  (requested: string[]): string[] => {
    const allowed = realLdapToSimulatableLdap(realLdap);
    return requested.every((group) => allowed.includes(group)) ? requested : [];
  };

export const simulatedLdapToEffectiveLdap =
  (simulatedLdap: string[]) =>
  (realLdap: string[]): string[] =>
    hasLength(simulatedLdap) ? simulatedLdap : realLdap;

export const simulatedLdapToHeaders = (simulatedLdap: string[]): Record<string, string> =>
  hasLength(simulatedLdap) ? { [SIMULATE_LDAP_GROUPS_HEADER]: ldapGroupsToStored(simulatedLdap) } : {};
